/**
 * A scenario as a reader wants it: numbered steps in words, each with the
 * comment that says why it is there, and the text as written one tab away.
 *
 * The words come from the same formatters the run page describes executed
 * steps with (humanize.ts), so a case reads the same before it runs and after.
 * The comments come from scenarioModel, which places them or, when unsure,
 * leaves them to the source tab rather than put one on the wrong step.
 */
import { load } from "js-yaml";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { clip, firstLine, formatAssert, formatInput } from "./humanize";
import { MODIFIERS, readScenario, type Note, type Prose, type ScenarioView, type StepView } from "./scenarioModel";
import { YamlSource } from "./YamlSource";

const isRect = (v: unknown) =>
  Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === "number");

/** The input with what does not fit on a line taken out: rectangle lists
    become their count, multi-line strings their first line - the scripts are
    returned separately, to be shown whole. */
function summarise(v: unknown, scripts: { label: string; text: string }[], label: string): unknown {
  if (typeof v === "string") {
    if (!v.includes("\n")) return v;
    scripts.push({ label, text: v });
    return firstLine(v);
  }
  if (Array.isArray(v)) {
    if (v.length && v.every(isRect)) return i18n.t("scenarioView.rects", { count: v.length });
    return v.map((x) => summarise(x, scripts, label));
  }
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v)) out[k] = summarise(x, scripts, k);
    return out;
  }
  return v;
}

function Paragraphs({ paras }: { paras: Prose[] }) {
  if (!paras.length) return null;
  return (
    <div className="space-y-1 border-l-2 border-sky-200 pl-2 text-slate-600 dark:border-sky-900 dark:text-slate-300">
      {paras.map((p, i) => (
        <p key={i}>
          {p.map((l, j) => (
            <span key={j} className="block whitespace-pre-wrap">
              {l}
            </span>
          ))}
        </p>
      ))}
    </div>
  );
}

/** What a note's line says, shortly: its key, an assertion in words, or the
    line itself clipped. */
function noteLabel(n: Note): string {
  if (n.key) return n.key;
  if (n.at.startsWith("{")) {
    try {
      const o = load(n.at);
      if (o && typeof o === "object" && !Array.isArray(o)) return clip(formatAssert(o), 60);
    } catch {
      // not a flow mapping after all - the text will do
    }
  }
  return clip(n.at, 60);
}

function Notes({ notes }: { notes: Note[] }) {
  if (!notes.length) return null;
  return (
    <ul className="space-y-0.5 text-slate-600 dark:text-slate-300">
      {notes.map((n, i) => (
        <li key={i} className="flex min-w-0 gap-2">
          <span className="shrink-0 font-mono text-[11px] text-slate-500 dark:text-slate-400">
            {noteLabel(n)}
          </span>
          <span className="min-w-0">{n.text.join(" ")}</span>
        </li>
      ))}
    </ul>
  );
}

function StepItem({ s }: { s: StepView }) {
  const { t } = useTranslation();
  const scripts: { label: string; text: string }[] = [];
  const said = formatInput(s.action, summarise(s.input, scripts, s.action))
    .replace(/\s+/g, " ")
    .trim();
  return (
    <li className="flex min-w-0 gap-2">
      <span className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded-full bg-slate-100 text-center text-[10px] font-semibold leading-4 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
        {s.n}
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="break-words">
          <span className="font-mono font-semibold text-brand-600 dark:text-sky-200">{s.action}</span>
          {said && <span className="ml-2 text-slate-800 dark:text-slate-200">{said}</span>}
          {s.sudo && (
            <span className="ml-2 rounded bg-slate-100 px-1 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {t("scenarioView.root")}
            </span>
          )}
        </div>
        <Paragraphs paras={s.why} />
        {scripts.map((sc, i) => (
          <details key={i}>
            <summary className="cursor-pointer text-slate-500 dark:text-slate-400">
              {t("scenarioView.script", { count: sc.text.replace(/\n+$/, "").split("\n").length })}
              {sc.label !== s.action ? ` · ${sc.label}` : ""}
            </summary>
            <pre className="mt-1 overflow-x-auto whitespace-pre rounded bg-white p-2 font-mono text-[11px] leading-snug text-slate-700 dark:bg-slate-900 dark:text-slate-300">
              {sc.text}
            </pre>
          </details>
        ))}
        {s.asserts.length > 0 && (
          <div className="flex flex-wrap items-baseline gap-1">
            <span className="font-medium text-slate-500">{t("scenarioView.checks")}</span>
            {s.asserts.map((a, i) => (
              <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                {formatAssert(a)}
              </span>
            ))}
          </div>
        )}
        {s.save != null && (
          <div>
            <span className="font-medium text-slate-500">{t("scenarioView.saves")}</span>{" "}
            <span className="font-mono text-[11px]">{formatInput(null, s.save)}</span>
          </div>
        )}
        {s.extra.map(([k, v]) => (
          <div key={k}>
            <span className="font-mono text-slate-500">{k}</span>{" "}
            <span className="font-mono text-[11px]">{formatInput(k, v)}</span>
          </div>
        ))}
        <Notes notes={s.notes} />
      </div>
    </li>
  );
}

/** One `restore` entry: what is put back, and what was saved to do it. */
function restoreLine(e: unknown): string {
  const act = (x: unknown) => {
    if (!x || typeof x !== "object" || Array.isArray(x)) return formatInput(null, x);
    const xo = x as Record<string, unknown>;
    const keys = Object.keys(xo);
    const k = keys.find((y) => !MODIFIERS.includes(y) && y !== "json") ?? keys[0];
    const main = `${xo.sudo === true ? `${i18n.t("scenarioView.root")}: ` : ""}${k} ${formatInput(k, xo[k])}`;
    return typeof xo.json === "string" ? `${main.trim()} → ${xo.json}` : main.trim();
  };
  if (!e || typeof e !== "object" || Array.isArray(e)) return formatInput(null, e);
  const o = e as Record<string, unknown>;
  if (o.apply === undefined) return act(o);
  const apply = act(o.apply);
  return o.capture !== undefined
    ? `${apply} (${i18n.t("scenarioView.captured", { what: act(o.capture) })})`
    : apply;
}

export function ScenarioReadable({ view }: { view: ScenarioView }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 text-xs" data-testid="scenario-readable">
      {(view.draft || view.runsLast) && (
        <div className="flex gap-1">
          {view.draft && (
            <span className="rounded bg-amber-100 px-1.5 text-[11px] text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              {t("scenarioView.draft")}
            </span>
          )}
          {view.runsLast && (
            <span className="rounded bg-slate-100 px-1.5 text-[11px] text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {t("scenarioView.runsLast")}
            </span>
          )}
        </div>
      )}
      {view.about && <p className="text-slate-700 dark:text-slate-300">{view.about}</p>}
      <Paragraphs paras={view.header} />
      <Notes notes={view.headerNotes} />
      {view.precondition.length > 0 && (
        <div className="space-y-1">
          <div className="font-medium text-slate-500">{t("scenarioView.precondition")}</div>
          <ol className="space-y-2">
            {view.precondition.map((s) => (
              <StepItem key={s.n} s={s} />
            ))}
          </ol>
        </div>
      )}
      <ol className="space-y-2">
        {view.steps.map((s) => (
          <StepItem key={s.n} s={s} />
        ))}
      </ol>
      {view.restore.length > 0 && (
        <div className="space-y-1">
          <div className="font-medium text-slate-500">{t("scenarioView.restore")}</div>
          <ul className="list-disc space-y-0.5 pl-5 font-mono text-[11px] text-slate-700 dark:text-slate-300">
            {view.restore.map((e, i) => (
              <li key={i} className="break-words">
                {restoreLine(e)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Paragraphs paras={view.after} />
      <Notes notes={view.afterNotes} />
      {!view.placed && <p className="text-slate-500">{t("scenarioView.unplaced")}</p>}
    </div>
  );
}

/** The view, memoised: the run page asks it for each executed step's why. */
export function useScenarioView(source?: string, verbs?: string[]): ScenarioView | null {
  const key = verbs?.join(" ") ?? "";
  // keyed on the words of `verbs`, not its identity: a fresh array each render
  // must not re-read the block
  return useMemo(() => (source ? readScenario(source, verbs) : null), [source, key]);
}

/**
 * The two ways to read a block, on tabs: in words (the default, when it can be
 * read) and as written. `sourceClassName` is the <pre>'s, as each page had it.
 */
export function ScenarioText({
  source,
  verbs,
  sourceClassName,
}: {
  source: string;
  verbs?: string[];
  sourceClassName: string;
}) {
  const { t } = useTranslation();
  const view = useScenarioView(source, verbs);
  const [tab, setTab] = useState<"readable" | "source">("readable");
  if (!view) return <YamlSource text={source} className={sourceClassName} />;
  const tabBtn = (k: "readable" | "source") => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === k}
      onClick={() => setTab(k)}
      className={`rounded border px-2 py-0.5 text-[11px] ${
        tab === k
          ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900"
          : "border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      }`}
    >
      {t(`scenarioView.${k}`)}
    </button>
  );
  return (
    <div className="space-y-1.5">
      <div role="tablist" className="flex gap-1">
        {tabBtn("readable")}
        {tabBtn("source")}
      </div>
      {tab === "readable" ? (
        <ScenarioReadable view={view} />
      ) : (
        <YamlSource text={source} className={sourceClassName} />
      )}
    </div>
  );
}
